import { z } from "zod";

export const TRAINING_GPU_TYPES = ["H100", "H200"] as const;
export const PROVIDER_GPU_TYPES = ["H100", "H200", "B40"] as const;
export const TRAINING_LR_SCHEDULERS = ["cosine", "linear", "constant"] as const;
export const TRAINING_ADAPTER_TYPES = ["lora"] as const;

export type TrainingGpuType = (typeof TRAINING_GPU_TYPES)[number];
export type ProviderGpuType = (typeof PROVIDER_GPU_TYPES)[number];

function stripUndefinedKeys<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined),
  ) as T;
}

export const TRAINING_NODE_COUNTS = [1, 2, 4] as const;

/**
 * A Hugging Face `org/name` repo id. Training base models must match it so the
 * runtime only ever resolves a Hub repo, never a local path inside the
 * training container. Mirrored by `HF_REPO_ID_PATTERN` in
 * `dojo/src/control_plane_payloads.py`.
 */
export const HF_REPO_ID_PATTERN =
  /^[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * Processor classes a training config may name. Axolotl resolves
 * `processor_type` with `getattr(transformers, name)`, so an open string lets a
 * recipe pick any transformers class. Mirrored by `ProcessorType` in
 * `dojo/src/control_plane_payloads.py`.
 */
export const TRAINING_PROCESSOR_TYPES = ["AutoProcessor"] as const;
const TRAINING_PROCESSOR_TYPE_SET: ReadonlySet<string> = new Set(
  TRAINING_PROCESSOR_TYPES,
);

/**
 * FSDP keys a training config may set; Dojo and Axolotl read nothing else.
 * Mirrored by `TRAINING_FSDP_CONFIG_KEYS` in `dojo/src/control_plane_payloads.py`.
 */
export const TRAINING_FSDP_CONFIG_KEYS = [
  "activation_checkpointing",
  "auto_wrap_policy",
  "cpu_offload_pin_memory",
  "cpu_ram_efficient_loading",
  "final_state_dict_type",
  "offload_params",
  "reshard_after_forward",
  "state_dict_type",
  "sync_module_states",
  "transformer_layer_cls_to_wrap",
  "use_orig_params",
] as const;
const TRAINING_FSDP_CONFIG_KEY_SET: ReadonlySet<string> = new Set(
  TRAINING_FSDP_CONFIG_KEYS,
);

const TrainingProcessorTypeSchema = z
  .string()
  .refine((value) => TRAINING_PROCESSOR_TYPE_SET.has(value), {
    message: `processor_type must be one of: ${TRAINING_PROCESSOR_TYPES.join(", ")}`,
  });

const TrainingFsdpConfigSchema = z
  .record(z.unknown())
  .refine(
    (config) =>
      Object.keys(config).every((key) => TRAINING_FSDP_CONFIG_KEY_SET.has(key)),
    {
      message: `fsdp_config keys must be among: ${TRAINING_FSDP_CONFIG_KEYS.join(", ")}`,
    },
  );

// Shared tuning fields used by both persisted recipe/preset config and
// resolved runtime launch config.
const TrainingConfigBaseSchema = z
  .object({
    adapter: z.enum(TRAINING_ADAPTER_TYPES).nullable().optional(),
    lora_r: z.number().int().optional(),
    lora_alpha: z.number().int().optional(),
    lora_dropout: z.number().optional(),
    lora_target_linear: z.boolean().optional(),
    sequence_len: z.number().int().optional(),
    micro_batch_size: z.number().int().optional(),
    gradient_accumulation_steps: z.number().int().optional(),
    max_steps: z.number().int().optional(),
    num_epochs: z.number().optional(),
    learning_rate: z.number().optional(),
    lr_scheduler: z.enum(TRAINING_LR_SCHEDULERS).optional(),
    warmup_ratio: z.number().optional(),
    bf16: z.boolean().optional(),
    flash_attention: z.boolean().optional(),
    gradient_checkpointing: z.boolean().optional(),
    dp_shard_size: z.number().int().optional(),
    dp_replicate_size: z.number().int().optional(),
    tensor_parallel_size: z.number().int().optional(),
    context_parallel_size: z.number().int().optional(),
    fsdp_version: z.number().int().optional(),
    fsdp_config: TrainingFsdpConfigSchema.optional(),
    dense_mixer: z.boolean().optional(),
    logging_steps: z.number().int().optional(),
    wandb_project: z.string().optional(),
    wandb_team: z.string().optional(),
    wandb_run_name: z.string().optional(),
    wandb_log_model: z.string().optional(),
    eval_every_n_steps: z.number().int().nullable().optional(),
    eval_at_steps: z.string().nullable().optional(),
    judge_temperature: z.number().optional(),
    judge_max_tokens: z.number().int().positive().optional(),
    disable_thinking: z.boolean().optional(),
    trust_remote_code: z.boolean().optional(),
    sample_packing: z.boolean().optional(),
    evals_per_epoch: z.number().int().positive().nullable().optional(),
    eval_at_end: z.boolean().optional(),
    // Only used for evaluation-time generation, not training itself.
    max_new_tokens: z.number().int().positive().optional(),
    generation_temperature: z.number().nonnegative().optional(),
    generation_top_p: z.number().positive().max(1).optional(),
    is_multimodal: z.boolean().optional(),
    processor_type: TrainingProcessorTypeSchema.optional(),
    skip_prepare_dataset: z.boolean().optional(),
  })
  .strict();

function validateEvalSchedule(
  data: Pick<
    z.infer<typeof TrainingConfigBaseSchema>,
    "eval_every_n_steps" | "eval_at_steps" | "evals_per_epoch"
  >,
  ctx: z.RefinementCtx,
) {
  const set = [
    data.eval_every_n_steps != null && data.eval_every_n_steps > 0,
    data.eval_at_steps != null && data.eval_at_steps.trim() !== "",
    data.evals_per_epoch != null && data.evals_per_epoch > 0,
  ].filter(Boolean).length;

  if (set > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "Only one of eval_every_n_steps, eval_at_steps, or evals_per_epoch may be set",
    });
  }
}

// Canonical persisted tuning config owned by recipes, presets, and training-job
// create/update APIs. Model identity does not live here.
export const TrainingConfigSchema =
  TrainingConfigBaseSchema.superRefine(validateEvalSchedule);

export type TrainingConfig = z.infer<typeof TrainingConfigSchema>;

// Resolved launch config persisted on training jobs and sent to dojo/providers.
// This extends the tuning config with runtime-only fields derived at launch time.
export const TrainingRuntimeConfigSchema = TrainingConfigBaseSchema.extend({
  base_model: z.string().trim().min(1, "Base model is required"),
  training_dataset_r2_path: z.string().trim().min(1),
  output_r2_prefix: z.string().trim().min(1),
  rubric_template: z.string().min(1),
  wandb_run_id: z.string().optional(),
  heartbeat_interval_seconds: z.number().int().positive().optional(),
  model_download_timeout_seconds: z.number().int().positive().optional(),
})
  .strict()
  .superRefine(validateEvalSchedule);

export type TrainingRuntimeConfig = z.infer<typeof TrainingRuntimeConfigSchema>;

export const TRAINING_EXPORT_MAX_TOKEN_MULTIPLIER = 2;

/**
 * Maximum allowed fraction of the training dataset that may overlap with the
 * eval dataset. Overlapping inferences are excluded from the exported training
 * artifact, so high overlap leaves too few unique samples to train on. A job
 * with overlap above this ratio is rejected at creation/retry time.
 */
export const MAX_TRAINING_EVAL_OVERLAP_RATIO = 0.8;

/**
 * We dont have an exact count of tokens in dataset, so we just auto filter out anything above 2x max sequence length
 * to avoid any huge requests slowing things down or making datsets unnecessarily large. Dojo later filters out any samples
 * above the sequence length so this is just an early measure to filter out anything huge.
 * @param sequenceLen
 * @returns
 */
export function getTrainingExportMaxTokens(sequenceLen: number): number {
  return sequenceLen * TRAINING_EXPORT_MAX_TOKEN_MULTIPLIER;
}

export const TrainingEvalAtStepsSchema = z
  .string()
  .optional()
  .transform((raw, ctx) => {
    if (!raw || raw.trim() === "") {
      return [] as number[];
    }

    const parsedSteps: number[] = [];
    for (const [index, part] of raw.split(",").entries()) {
      const parsed = z.coerce
        .number()
        .int()
        .nonnegative()
        .safeParse(part.trim());
      if (!parsed.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid eval_at_steps token at index ${index}`,
        });
        return z.NEVER;
      }
      parsedSteps.push(parsed.data);
    }

    return [...new Set(parsedSteps)];
  })
  .pipe(z.array(z.number().int().nonnegative()));

// Partial tuning config used by starter-template assets while they are still
// editable in the admin builder.
export const PartialTrainingConfigSchema = TrainingConfigBaseSchema.partial();
export type PartialTrainingConfig = z.infer<typeof PartialTrainingConfigSchema>;

export const TRAINING_DEFAULT_CONFIG = {
  lora_r: 16,
  lora_alpha: 32,
  lora_dropout: 0.05,
  lora_target_linear: true,
  sequence_len: 2048,
  micro_batch_size: 1,
  gradient_accumulation_steps: 4,
  max_steps: -1,
  num_epochs: 1,
  learning_rate: 0.0002,
  lr_scheduler: "cosine",
  warmup_ratio: 0.1,
  bf16: true,
  flash_attention: true,
  gradient_checkpointing: true,
  dp_shard_size: 0,
  dp_replicate_size: 1,
  tensor_parallel_size: 1,
  context_parallel_size: 1,
  fsdp_version: 2,
  fsdp_config: {},
  dense_mixer: false,
  logging_steps: 1,
  wandb_project: "inference-training",
  wandb_team: "inference-net",
  disable_thinking: false,
  trust_remote_code: false,
  sample_packing: false,
  eval_at_end: false,
  max_new_tokens: 8000,
} satisfies TrainingConfig;

export const TRAINING_PROVIDER_KINDS = [
  "k8s",
  "modal",
  "runpod",
  "manual",
] as const;

export type TrainingProviderKind = (typeof TRAINING_PROVIDER_KINDS)[number];

export const TrainingProviderKindSchema = z.enum(TRAINING_PROVIDER_KINDS);

export const TRAINING_DEPLOYMENT_MODES = ["provider", "manual"] as const;

export type TrainingDeploymentMode = (typeof TRAINING_DEPLOYMENT_MODES)[number];

export const TrainingDeploymentModeSchema = z.enum(TRAINING_DEPLOYMENT_MODES);

export const TRAINING_EVENT_TYPES = [
  "job.gpu_preflight.started",
  "job.gpu_preflight.completed",
  "job.artifact_download.started",
  "job.artifact_download.completed",
  "job.checkpoint_resume_download.started",
  "job.checkpoint_resume_download.completed",
  "job.model_download.started",
  "job.model_download.completed",
  "job.torchrun_init.started",
  "job.training.started",
  "job.heartbeat",
  "job.progress",
  "job.inference_export.started",
  "job.inference_export.completed",
  "job.checkpoint_upload.started",
  "job.checkpoint_upload.progress",
  "job.checkpoint_upload.completed",
  "job.checkpoint_upload.failed",
  "job.training.completed",
  "job.completed",
  "job.failed",
  "job.worker.error",
  "job.report.tokenization",
] as const;

export type TrainingEventType = (typeof TRAINING_EVENT_TYPES)[number];

export const TrainingEventTypeSchema = z.enum(TRAINING_EVENT_TYPES);

export const TRAINING_EVENT_SIGNATURE_HEADER = "x-training-event-signature";
export const TRAINING_EVENT_ALG_HEADER = "x-training-event-alg";
export const TRAINING_EVENT_VERSION_HEADER = "x-training-event-version";
export const TRAINING_EVENT_KEY_ID_HEADER = "x-training-event-key-id";
export const TRAINING_EVENT_SOURCE_HEADER = "x-training-event-source";

export const TrainingLogMetadataSchema = z
  .object({
    trainingJobId: z.string().trim().min(1).nullish(),
    trainingJobNodeId: z.string().trim().min(1).nullish(),
    trainingEvalId: z.string().trim().min(1).nullish(),
    nodeRank: z.number().int().nonnegative().nullish(),
    localRank: z.number().int().nonnegative().nullish(),
    globalRank: z.number().int().nonnegative().nullish(),
    numNodes: z.number().int().positive().nullish(),
    gpusPerNode: z.number().int().positive().nullish(),
    worldSize: z.number().int().positive().nullish(),
    hostname: z.string().trim().min(1).nullish(),
    ipAddress: z.string().trim().min(1).nullish(),
    loggerName: z.string().trim().min(1).nullish(),
    module: z.string().trim().min(1).nullish(),
    filename: z.string().trim().min(1).nullish(),
    functionName: z.string().trim().min(1).nullish(),
    lineNumber: z.number().int().positive().nullish(),
    processId: z.number().int().positive().nullish(),
    threadId: z.number().int().positive().nullish(),
    source: z.string().trim().min(1).nullish(),
    phase: z.string().trim().min(1).nullish(),
    extras: z.record(z.unknown()).nullish(),
  })
  .passthrough();

export const TrainingLogLineSchema = z.object({
  ts: z.string().datetime(),
  level: z.enum(["trace", "debug", "info", "warn", "error", "fatal"]),
  message: z.string().min(1),
  loggerName: z.string().min(1).nullish(),
  metadata: TrainingLogMetadataSchema.optional(),
});

export const TrainingMetricsSummarySchema = z
  .object({
    learningRate: z.number().optional(),
    gradNorm: z.number().optional(),
    throughputTokensPerSec: z.number().optional(),
    memoryUsedGb: z.number().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

export const TrainingModelDownloadStartedPayloadSchema = z
  .object({
    baseModel: z.string().min(1),
    timeoutSeconds: z.number().int().positive().optional(),
  })
  .strict();

export const TrainingModelDownloadCompletedPayloadSchema = z
  .object({
    baseModel: z.string().min(1),
    elapsedSeconds: z.number().nonnegative(),
  })
  .strict();

export const TrainingJobStartedPayloadSchema = z
  .object({
    totalSteps: z.number().int().nonnegative(),
  })
  .strict();

/**
 * Heartbeat payload — only carries runtime-discovered info.
 *
 * Cluster topology (nodeRank, numNodes, gpusPerNode, worldSize) is already
 * known from the launch request and stored at node creation time — no need
 * to echo it back in every heartbeat.
 */
export const WorkerStatusPayloadSchema = z.object({
  workerPidsAlive: z.number().int(),
  workerPidsExpected: z.number().int(),
  zombieCount: z.number().int(),
});

export const GpuInfoPayloadSchema = z.object({
  utilizationPct: z.number(),
  memoryUsedMb: z.number(),
  memoryTotalMb: z.number(),
  temperatureC: z.number(),
});

export const GpuHealthPayloadSchema = z.object({
  gpuCount: z.number().int(),
  gpuCountExpected: z.number().int(),
  perGpu: z.array(GpuInfoPayloadSchema),
});

export const HostMemoryPayloadSchema = z
  .object({
    usedMb: z.number(),
    totalMb: z.number(),
    availableMb: z.number(),
    swapUsedMb: z.number(),
    swapTotalMb: z.number(),
  })
  .strict();

export const WorkerDiagnosticPayloadSchema = z.object({
  globalRank: z.number().int(),
  localRank: z.number().int(),
  step: z.number().int().nullable().optional(),
  status: z.string(),
  loss: z.number().nullable().optional(),
  timestamp: z.string().nullable().optional(),
  gradNorm: z.number().nullable().optional(),
  memoryAllocatedMb: z.number().nullable().optional(),
  cudaPeakMemoryMb: z.number().nullable().optional(),
  errorType: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  errorDetails: z.string().nullable().optional(),
  faultCategory: z.string().nullable().optional(),
});

export const WorkerErrorPayloadSchema = z
  .object({
    globalRank: z.number().int(),
    localRank: z.number().int(),
    step: z.number().int().nullable().optional(),
    errorType: z.string(),
    errorMessage: z.string(),
    errorDetails: z.string().nullable().optional(),
    faultCategory: z.string().nullable().optional(),
  })
  .strict();

export const TrainingJobHeartbeatPayloadSchema = z.object({
  // Identifies which training_job_nodes row this heartbeat is for.
  trainingJobNodeId: z.string().min(1),
  // Per-process boot UUID generated at container startup.
  // Used to reject restarted containers for the same node.
  runtimeBootId: z.string().min(1),
  // Host identity — discovered at runtime, useful for log correlation.
  hostname: z.string().nullable(),
  ipAddress: z.string().nullable(),
  // Git commit SHA the dojo image was built from.
  buildCommit: z.string().nullable().optional(),
  // Phase awareness (from PhaseTracker)
  currentPhase: z.string().nullable().optional(),
  phaseElapsedSeconds: z.number().nullable().optional(),
  // Worker health (from WorkerMonitor)
  workerStatus: WorkerStatusPayloadSchema.nullable().optional(),
  // GPU health (from GpuHealthCollector)
  gpuHealth: GpuHealthPayloadSchema.nullable().optional(),
  // Host memory (from HostMemoryCollector)
  hostMemory: HostMemoryPayloadSchema.nullable().optional(),
  // Training progress (from phase file)
  lastTrainingStep: z.number().int().nullable().optional(),
  // Per-rank worker diagnostics (from WorkerDiagnosticsReader)
  workerDiagnostics: z
    .array(WorkerDiagnosticPayloadSchema)
    .nullable()
    .optional(),
});

export type TrainingJobHeartbeatPayload = z.infer<
  typeof TrainingJobHeartbeatPayloadSchema
>;

export const TrainingJobProgressPayloadSchema = z
  .object({
    currentStep: z.number().int().nonnegative(),
    totalSteps: z.number().int().positive(),
    currentLoss: z.number().nullable(),
    metrics: TrainingMetricsSummarySchema.nullable(),
  })
  .strict();

export const TrainingCheckpointTypeSchema = z.enum([
  "eval_export",
  "resumable",
  "final_export",
]);

export const TrainingEvalModelSourceKindSchema = z.enum([
  "checkpoint_r2",
  "base_model",
]);

export type TrainingEvalModelSourceKind = z.infer<
  typeof TrainingEvalModelSourceKindSchema
>;

export const TrainingJobCheckpointSavedPayloadSchema = z
  .object({
    step: z.number().int().nonnegative(),
    checkpointPathR2: z.string().min(1),
    checkpointType: TrainingCheckpointTypeSchema,
    isResumable: z.boolean(),
    shouldAutoEval: z.boolean(),
    isFinal: z.boolean().default(false),
    sizeBytes: z.number().int().nonnegative(),
  })
  .strict();

export const TrainingInferenceExportSourceKindSchema = z.enum(["final_model"]);

export const TrainingInferenceExportStartedPayloadSchema = z
  .object({
    step: z.number().int().nonnegative().nullable().optional(),
    checkpointType: TrainingCheckpointTypeSchema,
    sourcePath: z.string().min(1),
    sourceKind: TrainingInferenceExportSourceKindSchema,
    archiveName: z.string().min(1),
  })
  .strict();

export const TrainingInferenceExportCompletedPayloadSchema = z
  .object({
    step: z.number().int().nonnegative().nullable().optional(),
    checkpointType: TrainingCheckpointTypeSchema,
    sourcePath: z.string().min(1),
    sourceKind: TrainingInferenceExportSourceKindSchema,
    archiveName: z.string().min(1),
    fileCount: z.number().int().nonnegative(),
    sizeBytes: z.number().int().nonnegative(),
    elapsedSeconds: z.number().nonnegative(),
  })
  .strict();

export const TrainingJobArtifactDownloadedPayloadSchema = z
  .object({
    artifactType: z.string().min(1),
    artifactPathR2: z.string().min(1),
  })
  .strict();

export const TrainingJobTrainingCompletedPayloadSchema = z.object({}).strict();

export const TrainingJobCompletedPayloadSchema = z.object({}).strict();

export const TrainingJobFailedPayloadSchema = z
  .object({
    errorCode: z.string().min(1),
    errorMessage: z.string().min(1),
    errorDetails: z.string().nullable(),
    phase: z.string().nullable().optional(),
    phaseDurationSeconds: z.number().nullable().optional(),
    workerExitCodes: z.record(z.number()).nullable().optional(),
    signalInfo: z
      .object({
        signal: z.string(),
        receivedByRank: z.number().nullable().optional(),
        source: z.string(),
      })
      .nullable()
      .optional(),
    faultCategory: z.string().nullable().optional(),
    diagnosticSnapshotR2Path: z.string().nullable().optional(),
  })
  .strict();

export const TrainingLogBatchPayloadSchema = z
  .object({
    lines: z.array(TrainingLogLineSchema).min(1),
  })
  .strict();

// --- Report payload schemas (informational, not phase transitions) ---

export const TokenDetailSchema = z
  .object({
    tokenId: z.number().int(),
    text: z.string(),
    trainable: z.boolean(),
  })
  .strict();

export const TokenizationSampleSchema = z
  .object({
    percentile: z.number().int(),
    sequenceLength: z.number().int(),
    trainableTokens: z.number().int(),
    maskedTokens: z.number().int(),
    tokens: z.array(TokenDetailSchema),
  })
  .strict();

export const TokenizationReportPayloadSchema = z
  .object({
    totalExamples: z.number().int(),
    totalTokens: z.number().int(),
    totalTrainableTokens: z.number().int(),
    totalMaskedTokens: z.number().int(),
    trainableRatio: z.number(),
    sequenceLengthMin: z.number().int(),
    sequenceLengthMax: z.number().int(),
    sequenceLengthMean: z.number(),
    sequenceLengthP5: z.number().int(),
    sequenceLengthP50: z.number().int(),
    sequenceLengthP95: z.number().int(),
    sequenceLengthP99: z.number().int(),
    elapsedSeconds: z.number(),
    samples: z.array(TokenizationSampleSchema),
  })
  .strict();

export type TokenizationReportPayload = z.infer<
  typeof TokenizationReportPayloadSchema
>;

// --- Per-phase training payload schemas ---

const GpuPreflightStartedPayloadSchema = z.object({
  expectedGpus: z.number().int(),
});

const GpuPreflightCompletedPayloadSchema = z.object({
  expectedGpus: z.number().int(),
  detectedGpus: z.number().int(),
  elapsedSeconds: z.number().nonnegative(),
});

const ArtifactDownloadStartedPayloadSchema = z.object({
  artifactType: z.string().optional(),
  artifactPathR2: z.string().optional(),
});

const ArtifactDownloadCompletedPayloadSchema = z.object({
  artifactType: z.string().optional(),
  artifactPathR2: z.string().optional(),
  elapsedSeconds: z.number().optional(),
});

const CheckpointResumeDownloadStartedPayloadSchema = z.object({
  checkpointPathR2: z.string().optional(),
});

const CheckpointResumeDownloadCompletedPayloadSchema = z.object({
  checkpointPathR2: z.string().optional(),
  elapsedSeconds: z.number().optional(),
});

const TorchrunInitStartedPayloadSchema = z.object({
  numNodes: z.number().int().optional(),
  gpusPerNode: z.number().int().optional(),
  nodeRank: z.number().int().optional(),
});

const TrainingStartedPayloadSchema = z.object({
  totalSteps: z.number().int().optional(),
});

const TrainingCompletedPayloadSchema = z.object({});

const InferenceExportStartedPayloadSchema = z.object({
  step: z.number().int(),
  checkpointType: TrainingCheckpointTypeSchema,
  sourcePath: z.string().min(1),
  sourceKind: TrainingInferenceExportSourceKindSchema,
  archiveName: z.string().min(1),
});

const InferenceExportCompletedPayloadSchema = z.object({
  step: z.number().int(),
  checkpointType: TrainingCheckpointTypeSchema,
  sourcePath: z.string().min(1),
  sourceKind: TrainingInferenceExportSourceKindSchema,
  archiveName: z.string().min(1),
  fileCount: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  elapsedSeconds: z.number().nonnegative(),
});

const CheckpointUploadStartedPayloadSchema = z.object({
  step: z.number().int(),
  checkpointType: TrainingCheckpointTypeSchema,
  isResumable: z.boolean(),
  shouldAutoEval: z.boolean(),
  isFinal: z.boolean(),
});

const CheckpointUploadProgressPayloadSchema = z.object({
  step: z.number().int().nonnegative(),
  checkpointType: TrainingCheckpointTypeSchema,
  isResumable: z.boolean(),
  shouldAutoEval: z.boolean(),
  isFinal: z.boolean(),
  uploadedBytes: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  elapsedSeconds: z.number().nonnegative(),
});

export const TrainingArtifactKindSchema = z.enum([
  "full-weights",
  "lora-adapter",
]);

export type TrainingArtifactKind = z.infer<typeof TrainingArtifactKindSchema>;

const CheckpointUploadCompletedPayloadSchema = z
  .object({
    step: z.number().int().nonnegative(),
    checkpointPathR2: z.string().min(1),
    checkpointType: TrainingCheckpointTypeSchema,
    isResumable: z.boolean(),
    shouldAutoEval: z.boolean(),
    sizeBytes: z.number().int().nonnegative(),
    isFinal: z.boolean(),
    elapsedSeconds: z.number().nonnegative(),
    // What the uploaded inference export contains; null for resumable
    // checkpoint uploads (and absent from runtimes predating the field).
    artifactKind: TrainingArtifactKindSchema.nullish().default(null),
    // LoRA rank from the exported adapter_config.json; set exactly when
    // artifactKind is "lora-adapter" (enforced below).
    loraRank: z.number().int().positive().nullish().default(null),
  })
  .superRefine((payload, ctx) => {
    if (payload.artifactKind === "lora-adapter" && payload.loraRank == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'artifactKind "lora-adapter" requires loraRank',
        path: ["loraRank"],
      });
    }
    if (payload.artifactKind !== "lora-adapter" && payload.loraRank != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "loraRank is only valid for lora-adapter artifacts",
        path: ["loraRank"],
      });
    }
  });

const CheckpointUploadFailedPayloadSchema = z.object({
  step: z.number().int().nonnegative(),
  checkpointType: TrainingCheckpointTypeSchema,
  isResumable: z.boolean(),
  isFinal: z.boolean(),
  reason: z.string().min(1),
  elapsedSeconds: z.number().nonnegative(),
});

// --- Per-phase eval payload schemas ---

const EvalGpuPreflightStartedPayloadSchema = z.object({
  expectedGpus: z.number().int().optional(),
});

const EvalGpuPreflightCompletedPayloadSchema = z.object({
  expectedGpus: z.number().int().optional(),
  detectedGpus: z.number().int().optional(),
  elapsedSeconds: z.number().optional(),
});

const EvalArtifactDownloadStartedPayloadSchema = z.object({
  modelSourceKind: TrainingEvalModelSourceKindSchema.optional(),
  modelSourceRef: z.string().min(1).optional(),
  validationR2Path: z.string().optional(),
});

const EvalArtifactDownloadCompletedPayloadSchema = z.object({
  modelSourceKind: TrainingEvalModelSourceKindSchema.optional(),
  modelSourceRef: z.string().min(1).optional(),
  validationR2Path: z.string().optional(),
  elapsedSeconds: z.number().optional(),
});

const EvalInferenceStartedPayloadSchema = z.object({});

const EvalInferenceCompletedPayloadSchema = z.object({
  elapsedSeconds: z.number().optional(),
});

const EvalResultUploadStartedPayloadSchema = z.object({
  outputR2Path: z.string().optional(),
});

const EvalResultUploadCompletedPayloadSchema = z.object({
  outputR2Path: z.string().optional(),
  elapsedSeconds: z.number().optional(),
});

const TrainingEventEnvelopeBaseSchema = z
  .object({
    version: z.literal("v1"),
    eventId: z.string().uuid(),
    jobId: z.string().min(1),
    launchSessionId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
    occurredAt: z.string().datetime(),
    signature: z.string().min(1).optional(),
  })
  .strict();

const trainingEventEnvelopeFor = <
  EventType extends TrainingEventType,
  PayloadSchema extends z.ZodTypeAny,
>(
  eventType: EventType,
  payloadSchema: PayloadSchema,
) =>
  TrainingEventEnvelopeBaseSchema.extend({
    eventType: z.literal(eventType),
    payload: payloadSchema,
  }).strict();

export const TrainingModelDownloadStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.model_download.started",
    TrainingModelDownloadStartedPayloadSchema,
  );

export const TrainingModelDownloadCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.model_download.completed",
    TrainingModelDownloadCompletedPayloadSchema,
  );

export const TrainingGpuPreflightStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.gpu_preflight.started",
    GpuPreflightStartedPayloadSchema,
  );

export const TrainingGpuPreflightCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.gpu_preflight.completed",
    GpuPreflightCompletedPayloadSchema,
  );

export const TrainingArtifactDownloadStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.artifact_download.started",
    ArtifactDownloadStartedPayloadSchema,
  );

export const TrainingArtifactDownloadCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.artifact_download.completed",
    ArtifactDownloadCompletedPayloadSchema,
  );

export const TrainingCheckpointResumeDownloadStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_resume_download.started",
    CheckpointResumeDownloadStartedPayloadSchema,
  );

export const TrainingCheckpointResumeDownloadCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_resume_download.completed",
    CheckpointResumeDownloadCompletedPayloadSchema,
  );

export const TrainingTorchrunInitStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.torchrun_init.started",
    TorchrunInitStartedPayloadSchema,
  );

export const TrainingStartedEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.training.started",
  TrainingStartedPayloadSchema,
);

export const TrainingHeartbeatEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.heartbeat",
  TrainingJobHeartbeatPayloadSchema,
);

export const TrainingProgressEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.progress",
  TrainingJobProgressPayloadSchema,
);

export const TrainingInferenceExportStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.inference_export.started",
    InferenceExportStartedPayloadSchema,
  );

export const TrainingInferenceExportCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.inference_export.completed",
    InferenceExportCompletedPayloadSchema,
  );

export const TrainingCheckpointUploadStartedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_upload.started",
    CheckpointUploadStartedPayloadSchema,
  );

export const TrainingCheckpointUploadProgressEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_upload.progress",
    CheckpointUploadProgressPayloadSchema,
  );

export const TrainingCheckpointUploadCompletedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_upload.completed",
    CheckpointUploadCompletedPayloadSchema,
  );

export const TrainingCheckpointUploadFailedEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.checkpoint_upload.failed",
    CheckpointUploadFailedPayloadSchema,
  );

export const TrainingCompletedEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.training.completed",
  TrainingCompletedPayloadSchema,
);

export const TrainingJobCompletedEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.completed",
  TrainingJobCompletedPayloadSchema,
);

export const TrainingFailedEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.failed",
  TrainingJobFailedPayloadSchema,
);

export const TrainingWorkerErrorEventEnvelopeSchema = trainingEventEnvelopeFor(
  "job.worker.error",
  WorkerErrorPayloadSchema,
);

export const TrainingTokenizationReportEventEnvelopeSchema =
  trainingEventEnvelopeFor(
    "job.report.tokenization",
    TokenizationReportPayloadSchema,
  );

export const TrainingEventEnvelopeSchema = z.discriminatedUnion("eventType", [
  TrainingGpuPreflightStartedEventEnvelopeSchema,
  TrainingGpuPreflightCompletedEventEnvelopeSchema,
  TrainingArtifactDownloadStartedEventEnvelopeSchema,
  TrainingArtifactDownloadCompletedEventEnvelopeSchema,
  TrainingCheckpointResumeDownloadStartedEventEnvelopeSchema,
  TrainingCheckpointResumeDownloadCompletedEventEnvelopeSchema,
  TrainingModelDownloadStartedEventEnvelopeSchema,
  TrainingModelDownloadCompletedEventEnvelopeSchema,
  TrainingTorchrunInitStartedEventEnvelopeSchema,
  TrainingStartedEventEnvelopeSchema,
  TrainingHeartbeatEventEnvelopeSchema,
  TrainingProgressEventEnvelopeSchema,
  TrainingInferenceExportStartedEventEnvelopeSchema,
  TrainingInferenceExportCompletedEventEnvelopeSchema,
  TrainingCheckpointUploadStartedEventEnvelopeSchema,
  TrainingCheckpointUploadProgressEventEnvelopeSchema,
  TrainingCheckpointUploadCompletedEventEnvelopeSchema,
  TrainingCheckpointUploadFailedEventEnvelopeSchema,
  TrainingCompletedEventEnvelopeSchema,
  TrainingJobCompletedEventEnvelopeSchema,
  TrainingFailedEventEnvelopeSchema,
  TrainingWorkerErrorEventEnvelopeSchema,
  TrainingTokenizationReportEventEnvelopeSchema,
]);

export type TrainingEventEnvelope = z.infer<typeof TrainingEventEnvelopeSchema>;

export const EVAL_CALLBACK_EVENT_TYPES = [
  "eval.gpu_preflight.started",
  "eval.gpu_preflight.completed",
  "eval.artifact_download.started",
  "eval.artifact_download.completed",
  "eval.inference.started",
  "eval.inference.completed",
  "eval.result_upload.started",
  "eval.result_upload.completed",
  "eval.heartbeat",
  "eval.completed",
  "eval.failed",
] as const;

export type EvalCallbackEventType = (typeof EVAL_CALLBACK_EVENT_TYPES)[number];

export const EvalCallbackEventTypeSchema = z.enum(EVAL_CALLBACK_EVENT_TYPES);

/** Base envelope for training eval callbacks (mid-training auto evals). */
const TrainingEvalCallbackEnvelopeBaseSchema = z
  .object({
    version: z.literal("v1"),
    eventId: z.string().uuid(),
    eventType: EvalCallbackEventTypeSchema,
    trainingEvalId: z.string().min(1),
    trainingJobId: z.string().min(1),
    occurredAt: z.string().datetime(),
  })
  .strict();

/** Base envelope for manual eval callbacks (eval runs against trained artifacts). */
const ManualEvalCallbackEnvelopeBaseSchema = z
  .object({
    version: z.literal("v1"),
    eventId: z.string().uuid(),
    eventType: EvalCallbackEventTypeSchema,
    evalRunId: z.string().min(1),
    evalRunGroupId: z.string().min(1),
    occurredAt: z.string().datetime(),
  })
  .strict();

export const EvalScoreDistributionSchema = z.record(z.number());

export const EvalCallbackCompletedPayloadSchema = z
  .object({
    status: z.literal("completed").default("completed"),
    modelSourceKind: TrainingEvalModelSourceKindSchema,
    modelSourceRef: z.string().min(1),
    validationR2Path: z.string(),
    successfulSamples: z.number().int().nonnegative(),
    failedSamples: z.number().int().nonnegative(),
    runId: z.string(),
    checkpointStep: z.number().int().nonnegative(),
    metricsR2Path: z.string(),
    samplesR2Path: z.string(),
  })
  .strict();

export const EvalCallbackHeartbeatPayloadSchema = z
  .object({
    buildCommit: z.string().nullable().optional(),
    currentPhase: z.string().nullable().optional(),
    phaseElapsedSeconds: z.number().nullable().optional(),
    gpuHealth: GpuHealthPayloadSchema.nullable().optional(),
    gpuCount: z.number().int().optional(),
  })
  .strict();

export const EvalCallbackFailedPayloadSchema = z
  .object({
    status: z.literal("failed").default("failed"),
    errorCode: z.string().nullable(),
    errorMessage: z.string().min(1),
    errorDetails: z.string().nullable(),
    metricsR2Path: z.string().optional(),
    samplesR2Path: z.string().optional(),
    phase: z.string().nullable().optional(),
    phaseDurationSeconds: z.number().nullable().optional(),
    faultCategory: z.string().nullable().optional(),
    diagnosticSnapshotR2Path: z.string().nullable().optional(),
  })
  .strict();

// --- Training eval callback envelopes (mid-training auto evals) ---

export const EvalHeartbeatCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.heartbeat"),
    payload: EvalCallbackHeartbeatPayloadSchema,
  }).strict();

export const EvalCompletedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.completed"),
    payload: EvalCallbackCompletedPayloadSchema,
  }).strict();

export const EvalFailedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.failed"),
    payload: EvalCallbackFailedPayloadSchema,
  }).strict();

export const EvalGpuPreflightStartedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.gpu_preflight.started"),
    payload: EvalGpuPreflightStartedPayloadSchema,
  }).strict();

export const EvalGpuPreflightCompletedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.gpu_preflight.completed"),
    payload: EvalGpuPreflightCompletedPayloadSchema,
  }).strict();

export const EvalArtifactDownloadStartedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.artifact_download.started"),
    payload: EvalArtifactDownloadStartedPayloadSchema,
  }).strict();

export const EvalArtifactDownloadCompletedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.artifact_download.completed"),
    payload: EvalArtifactDownloadCompletedPayloadSchema,
  }).strict();

export const EvalInferenceStartedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.inference.started"),
    payload: EvalInferenceStartedPayloadSchema,
  }).strict();

export const EvalInferenceCompletedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.inference.completed"),
    payload: EvalInferenceCompletedPayloadSchema,
  }).strict();

export const EvalResultUploadStartedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.result_upload.started"),
    payload: EvalResultUploadStartedPayloadSchema,
  }).strict();

export const EvalResultUploadCompletedCallbackEnvelopeSchema =
  TrainingEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.result_upload.completed"),
    payload: EvalResultUploadCompletedPayloadSchema,
  }).strict();

/** Training eval callback envelope (mid-training auto evals). */
export const TrainingEvalCallbackEnvelopeSchema = z.discriminatedUnion(
  "eventType",
  [
    EvalGpuPreflightStartedCallbackEnvelopeSchema,
    EvalGpuPreflightCompletedCallbackEnvelopeSchema,
    EvalArtifactDownloadStartedCallbackEnvelopeSchema,
    EvalArtifactDownloadCompletedCallbackEnvelopeSchema,
    EvalInferenceStartedCallbackEnvelopeSchema,
    EvalInferenceCompletedCallbackEnvelopeSchema,
    EvalResultUploadStartedCallbackEnvelopeSchema,
    EvalResultUploadCompletedCallbackEnvelopeSchema,
    EvalHeartbeatCallbackEnvelopeSchema,
    EvalCompletedCallbackEnvelopeSchema,
    EvalFailedCallbackEnvelopeSchema,
  ],
);

export type TrainingEvalCallbackEnvelope = z.infer<
  typeof TrainingEvalCallbackEnvelopeSchema
>;

// --- Manual eval callback envelopes (eval runs against trained artifacts) ---

const ManualEvalHeartbeatEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.heartbeat"),
    payload: EvalCallbackHeartbeatPayloadSchema,
  }).strict();

const ManualEvalCompletedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.completed"),
    payload: EvalCallbackCompletedPayloadSchema,
  }).strict();

const ManualEvalFailedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.failed"),
    payload: EvalCallbackFailedPayloadSchema,
  }).strict();

const ManualEvalGpuPreflightStartedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.gpu_preflight.started"),
    payload: EvalGpuPreflightStartedPayloadSchema,
  }).strict();

const ManualEvalGpuPreflightCompletedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.gpu_preflight.completed"),
    payload: EvalGpuPreflightCompletedPayloadSchema,
  }).strict();

const ManualEvalArtifactDownloadStartedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.artifact_download.started"),
    payload: EvalArtifactDownloadStartedPayloadSchema,
  }).strict();

const ManualEvalArtifactDownloadCompletedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.artifact_download.completed"),
    payload: EvalArtifactDownloadCompletedPayloadSchema,
  }).strict();

const ManualEvalInferenceStartedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.inference.started"),
    payload: EvalInferenceStartedPayloadSchema,
  }).strict();

const ManualEvalInferenceCompletedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.inference.completed"),
    payload: EvalInferenceCompletedPayloadSchema,
  }).strict();

const ManualEvalResultUploadStartedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.result_upload.started"),
    payload: EvalResultUploadStartedPayloadSchema,
  }).strict();

const ManualEvalResultUploadCompletedEnvelopeSchema =
  ManualEvalCallbackEnvelopeBaseSchema.extend({
    eventType: z.literal("eval.result_upload.completed"),
    payload: EvalResultUploadCompletedPayloadSchema,
  }).strict();

/** Manual eval callback envelope (eval runs against trained artifacts). */
export const ManualEvalCallbackEnvelopeSchema = z.discriminatedUnion(
  "eventType",
  [
    ManualEvalGpuPreflightStartedEnvelopeSchema,
    ManualEvalGpuPreflightCompletedEnvelopeSchema,
    ManualEvalArtifactDownloadStartedEnvelopeSchema,
    ManualEvalArtifactDownloadCompletedEnvelopeSchema,
    ManualEvalInferenceStartedEnvelopeSchema,
    ManualEvalInferenceCompletedEnvelopeSchema,
    ManualEvalResultUploadStartedEnvelopeSchema,
    ManualEvalResultUploadCompletedEnvelopeSchema,
    ManualEvalHeartbeatEnvelopeSchema,
    ManualEvalCompletedEnvelopeSchema,
    ManualEvalFailedEnvelopeSchema,
  ],
);

export type ManualEvalCallbackEnvelope = z.infer<
  typeof ManualEvalCallbackEnvelopeSchema
>;

/** Union schema for webhook parsing — accepts either training or manual eval envelopes. */
export const EvalCallbackEnvelopeSchema = z.union([
  TrainingEvalCallbackEnvelopeSchema,
  ManualEvalCallbackEnvelopeSchema,
]);

export type EvalCallbackEnvelope = z.infer<typeof EvalCallbackEnvelopeSchema>;

export const TrainingWebhookEnvelopeSchema = z.union([
  TrainingEventEnvelopeSchema,
  EvalCallbackEnvelopeSchema,
]);

export type TrainingWebhookEnvelope = z.infer<
  typeof TrainingWebhookEnvelopeSchema
>;

export const ProviderCapabilitiesSchema = z
  .object({
    // Hard provider cutoff for a single launched training session.
    maxSessionLifetimeSeconds: z.number().int().positive().nullable(),
    // Buffer before the hard cutoff when the control plane should request rollover.
    rotateBufferSeconds: z.number().int().positive().nullable(),
    // Largest multi-node topology this provider can launch.
    maxNodes: z.number().int().positive(),
    // Largest per-node GPU count this provider can launch.
    maxGpusPerNode: z.number().int().positive(),
    // Training GPU types this provider can satisfy.
    supportedTrainingGpuTypes: z.array(z.enum(TRAINING_GPU_TYPES)).min(1),
    // Eval GPU types this provider can satisfy.
    supportedEvalGpuTypes: z.array(z.enum(PROVIDER_GPU_TYPES)).min(1),
    // How long a training launch may stay in startup before watchdog timeout.
    trainingStartupTimeoutSeconds: z.number().int().positive(),
    // How long training may go without heartbeats before watchdog timeout.
    trainingHeartbeatStaleSeconds: z.number().int().positive(),
    // How long an eval launch may stay in startup before watchdog timeout.
    evalStartupTimeoutSeconds: z.number().int().positive(),
    // How long eval may go without heartbeats before watchdog timeout.
    evalHeartbeatStaleSeconds: z.number().int().positive(),
    // Hard runtime cap for eval runs.
    evalMaxRuntimeSeconds: z.number().int().positive(),
  })
  .strict();

export type ProviderCapabilities = z.infer<typeof ProviderCapabilitiesSchema>;

// Wire-mirror of `dojo/src/control_plane_payloads.py` `LaunchTrainingPayload`.
// The mirror is manual (no codegen) and both sides are strict, so any shape
// change must land in both files; grep for the other side before editing.
// Every field here exists on the Python side; a few Python-side optionals are
// launcher-settable and not yet sent from this schema.
export const LaunchTrainingRunRequestSchema = z
  .object({
    // Job metadata
    jobId: z.string().min(1),
    // W3C trace context of the control-plane trace that launched the job;
    // the Dojo runtime parents its root span to it so container spans join
    // the same distributed trace as the TypeScript services.
    traceparent: z.string().optional(),
    resumeFromCheckpointR2Path: z.string().optional(),

    // Callback
    callbackUrl: z.string().url(),
    callbackScheme: z.literal("hmac-sha256").default("hmac-sha256"),
    callbackSecret: z.string().min(1),
    callbackKeyId: z.string().optional(),
    launchSessionId: z.string().uuid(),

    // R2
    r2Endpoint: z.string().url(),
    r2Bucket: z.string().min(1),
    r2AccessKeyId: z.string().min(1),
    r2SecretAccessKey: z.string().min(1),

    // Resources
    numNodes: z.number().int().positive().default(1),
    gpusPerNode: z.number().int().positive().default(1),

    // Distributed topology (per-node, set by orchestrator/provider)
    nodeRank: z.number().int().nonnegative(),

    // Per-node tracking
    trainingJobNodeId: z.string().min(1),

    // Training core
    baseModel: z.string().regex(HF_REPO_ID_PATTERN),
    adapter: z.enum(TRAINING_ADAPTER_TYPES).nullable().optional(),
    datasetR2Path: z.string().min(1),
    outputR2Prefix: z.string().min(1),
    maxSteps: z.number().int().optional(),
    numEpochs: z.number().optional(),
    microBatchSize: z.number().int().positive().default(1),
    gradientAccumulationSteps: z.number().int().positive().default(1),
    learningRate: z.number().positive().default(0.0002),
    seqLength: z.number().int().positive().default(2048),

    // LoRA / PEFT
    loraR: z.number().int().optional(),
    loraAlpha: z.number().int().optional(),
    loraDropout: z.number().optional(),
    loraTargetLinear: z.boolean().optional(),

    // Optimizer / Scheduler
    lrScheduler: z.enum(TRAINING_LR_SCHEDULERS).optional(),
    warmupRatio: z.number().optional(),

    // Precision
    bf16: z.boolean().optional(),

    // Performance
    flashAttention: z.boolean().optional(),

    // Distributed / Parallelism
    dpShardSize: z.number().int().optional(),
    dpReplicateSize: z.number().int().optional(),
    tensorParallelSize: z.number().int().optional(),
    contextParallelSize: z.number().int().optional(),
    fsdpVersion: z.number().int().optional(),
    fsdpConfig: TrainingFsdpConfigSchema.optional(),

    // MoE
    denseMixer: z.boolean().optional(),

    samplePacking: z.boolean().optional(),

    // Multimodal
    isMultimodal: z.boolean().optional(),
    processorType: TrainingProcessorTypeSchema.optional(),
    skipPrepareDataset: z.boolean().optional(),

    // Logging / Monitoring
    loggingSteps: z.number().int().positive().default(1),
    wandbProject: z.string().optional(),
    wandbTeam: z.string().optional(),
    wandbRunName: z.string().optional(),
    wandbRunId: z.string().optional(),
    wandbLogModel: z.string().optional(),

    // Model loading
    trustRemoteCode: z.boolean().optional(),

    // Checkpoint scheduling
    evalEveryNSteps: z.number().int().positive().nullable().default(null),
    evalAtSteps: z.string().min(1).nullable().default(null),
    evalsPerEpoch: z.number().int().positive().nullable().default(null),
    evalAtEnd: z.boolean().default(false),
    backupCheckpointsPerEpoch: z.number().int().positive().default(2),

    // Runtime
    heartbeatIntervalSeconds: z.number().positive().default(30),
    modelDownloadTimeoutSeconds: z.number().int().positive().optional(),

    // Diagnostics
    debug: z.boolean().optional(),
    diagnosticSnapshotR2Path: z.string().min(1),

    // API keys (injected into runtime env vars)
    hfToken: z.string().min(1),
    wandbApiKey: z.string().min(1),

    // OTEL — runtime ships logs/traces/metrics to external telemetry via
    // OTLP HTTP using this endpoint + token.
    //
    // - `otelEndpoint` is the public OTLP base URL Bellweather returned
    //   from `RegisterTelemetrySource` (NOT the admin API URL the
    //   control plane uses to mint tokens).
    // - `otelAuthToken` is the per-runtime JWT Bellweather issued.
    //
    // Source id and service name are derived at runtime from
    // trainingJobId (see `OtelResourceAttributes.source_id`/
    // `service_name`), so they don't travel over the wire.
    otelEndpoint: z.string().url(),
    otelAuthToken: z.string().min(1),
  })
  .strict()
  .transform((payload) =>
    stripUndefinedKeys({
      ...payload,
      modelDownloadTimeoutSeconds: payload.modelDownloadTimeoutSeconds ?? 1800,
    }),
  );

export type LaunchTrainingRunRequest = z.infer<
  typeof LaunchTrainingRunRequestSchema
>;

export interface LaunchTrainingRunResult {
  providerRunId: string;
  providerContainerId?: string;
  providerTarget?: string;
}

export const StopTrainingRunRequestSchema = z
  .object({
    jobId: z.string().min(1),
    provider: TrainingProviderKindSchema,
    providerRunId: z.string().min(1),
    providerTarget: z.string().min(1).nullable().optional(),
    reason: z.string().optional(),
  })
  .strict();

export type StopTrainingRunRequest = z.infer<
  typeof StopTrainingRunRequestSchema
>;

export const LaunchFromCheckpointRequestSchema = z
  .object({
    jobId: z.string().min(1),
    provider: TrainingProviderKindSchema,
    checkpointR2Path: z.string().min(1),
    callbackUrl: z.string().url(),
  })
  .strict();

const LaunchEvalBaseFields = {
  // Job metadata
  heartbeatIntervalSeconds: z.number().positive().default(30),
  debug: z.boolean().optional(),

  // Callback
  callbackUrl: z.string().url(),
  callbackScheme: z.literal("hmac-sha256").default("hmac-sha256"),
  callbackSecret: z.string().min(1),
  callbackKeyId: z.string().optional(),

  // R2
  r2Endpoint: z.string().url(),
  r2Bucket: z.string().min(1),
  r2AccessKeyId: z.string().min(1),
  r2SecretAccessKey: z.string().min(1),
  hfToken: z.string().min(1),

  // Model source
  modelSourceKind: TrainingEvalModelSourceKindSchema,
  modelSourceRef: z.string().min(1),
  checkpointStep: z.number().int().nonnegative(),

  // Paths
  validationR2Path: z.string().min(1),
  outputR2Prefix: z.string().min(1),

  // Inference config
  gpuCount: z.number().int().positive(),
  // Keep this payload in sync with the OnlineEvalVllmFlags model.
  vllmEngineFlags: z.record(z.string().min(1), z.string()),

  // Generation config
  maxNewTokens: z
    .number()
    .int()
    .positive()
    .default(TRAINING_DEFAULT_CONFIG.max_new_tokens),
  generationTemperature: z.number().nonnegative().default(0.1),
  generationTopP: z.number().positive().max(1).default(0.95),
  generationBatchSize: z.number().int().positive().default(32),

  // Sampling
  maxValidationSamples: z.number().int().positive().optional(),
  disableThinking: z.boolean().default(false),

  // OTEL — public OTLP endpoint + per-runtime JWT, both returned by
  // Bellweather's `RegisterTelemetrySource`. See
  // `LaunchTrainingRunRequestSchema` for full rationale.
  otelEndpoint: z.string().url(),
  otelAuthToken: z.string().min(1),
  // W3C trace context of the control-plane trace that launched the eval;
  // the Dojo runtime parents its root span to it so container spans join
  // the same distributed trace as the TypeScript services.
  traceparent: z.string().optional(),
} as const;

/** Dojo eval launch correlated to a mid-training eval row.
 *
 * Dojo runs generation-only. Judge scoring is performed by LLM Ops after
 * Dojo reports `eval.completed`. */
export const LaunchTrainingEvalRequestSchema = z
  .object({
    ...LaunchEvalBaseFields,
    trainingEvalId: z.string().min(1),
    trainingJobId: z.string().min(1),
  })
  .strict()
  .transform((payload) => stripUndefinedKeys(payload));

/** Dojo eval launch correlated to a manual eval run. */
export const LaunchManualEvalRequestSchema = z
  .object({
    ...LaunchEvalBaseFields,
    evalRunId: z.string().min(1),
    evalRunGroupId: z.string().min(1),
  })
  .strict()
  .transform((payload) => stripUndefinedKeys(payload));

/** Union schema that accepts either variant. */
export const LaunchEvalRequestSchema = z.union([
  LaunchTrainingEvalRequestSchema,
  LaunchManualEvalRequestSchema,
]);

export type LaunchEvalRequest = z.infer<typeof LaunchEvalRequestSchema>;
export type LaunchTrainingEvalRequest = z.infer<
  typeof LaunchTrainingEvalRequestSchema
>;
export type LaunchManualEvalRequest = z.infer<
  typeof LaunchManualEvalRequestSchema
>;

export interface LaunchEvalResult {
  providerRunId?: string;
  providerContainerId?: string;
  providerTarget?: string;
}

export const CheckpointRefSchema = z
  .object({
    jobId: z.string().min(1),
    step: z.number().int().nonnegative(),
    checkpointR2Path: z.string().min(1),
    isFinal: z.boolean().default(false),
  })
  .strict();

export type CheckpointRef = z.infer<typeof CheckpointRefSchema>;
